import moment from 'moment-timezone';
import { STATUS } from 'src/constants/common';

import { COLUMN_CLASSNAME_MAP, COLUMN_TRANSLATION_MAP, IS_CP_PICKUP_DATE_UPDATE_ENABLED } from './myQuote.const';
import { IQuoteTab } from './myQuote.interface';

export type QuoteActionKey = 'update_pickup_date';

export interface QuoteActionType {
    actionId: number;
    actionKey: QuoteActionKey;
    actionButtonName: string;
    actionCaption: string;
    actionDescription: string;
}

/**
 * Customer-portal quick actions for a quote row, mirroring prepareQuoteActionInformation in
 * wal-frontend-v2's QuotingHub/QuotingDashboard/quotingDashboard.helpers.ts. Currently the only
 * action is prompting the customer to update a pickup date that has passed (not applicable once
 * the quote is Booked, mirroring QuoteDetailMetaDataTable's own past-date check).
 */
export const prepareQuoteActionInformation = (quote: any, t: (key: string, opts?: any) => string, timezone?: string): QuoteActionType[] => {
    const actions: QuoteActionType[] = [];
    let actionId = 1;

    const isPickupDatePast = (() => {
        // Whole flow is feature-flagged off for CP — see IS_CP_PICKUP_DATE_UPDATE_ENABLED.
        if (!IS_CP_PICKUP_DATE_UPDATE_ENABLED) return false;
        if (!quote?.pickupDate || quote?.status === STATUS.BOOKED) return false;
        const pickupDateString = moment.utc(quote.pickupDate).format('YYYY-MM-DD');
        const todayString = moment()
            .tz(timezone || moment.tz.guess())
            .startOf('day')
            .format('YYYY-MM-DD');

        return pickupDateString < todayString;
    })();

    if (isPickupDatePast) {
        actions.push({
            actionId: actionId++,
            actionKey: 'update_pickup_date',
            actionButtonName: t('QuotingHub.QuotingDashboard.Update_Pickup_Date'),
            actionCaption: t('QuotingHub.QuotingDashboard.Update_Pickup_Date'),
            actionDescription: t('QuotingHub.QuotingDashboard.Update_Pickup_Date_desc'),
        });
    }

    return actions;
};

// Mirrors wal-frontend-v2's getActionColumnWidthClass: count the action icons a quote row will
// render (same conditions as QuoteCustomerRow's action cell) and return an exact Tailwind
// arbitrary-value class for the widest row. Keep these conditions in sync with the row.
const ACTION_ICON_PX = 16;
const ACTION_GAP_PX = 6;
const ACTION_PADDING_PX = 24; // 12px left + 12px right (--table-col-space-common-xs-leftrightmost)

export const getActionColumnWidthClass = (quotes: any[]): string => {
    if (!quotes?.length) return '';

    let maxWidth = 0;

    quotes.forEach((quote: any) => {
        const widths: number[] = [];

        // edit icon — hidden for won/lost/booked quotes (same condition as QuoteCustomerRow's editButton)
        if (quote?.status !== STATUS.WON && quote?.status !== STATUS.LOST && quote?.status !== STATUS.BOOKED) widths.push(ACTION_ICON_PX);

        if (!widths.length) return; // no action icons — contributes w-0

        const contentWidth = widths.reduce((sum, w) => sum + w, 0) + Math.max(0, widths.length - 1) * ACTION_GAP_PX;
        maxWidth = Math.max(maxWidth, contentWidth + ACTION_PADDING_PX);
    });

    return maxWidth ? `w-[${maxWidth}px]` : '!p-0';
};

/**
 * Transforms column config array into headCells format for TableCmp.
 * Ensures 'action' column is always last and applies translations + CSS classes.
 */
export const transformColumnsToHeadCells = (defaultColumns: any[], hasData: boolean, t: (key: string) => string, actionColumnWidthClass?: string) => {
    const sortedColumns = [...defaultColumns].sort((a, b) => a.displayOrder - b.displayOrder);

    // Ensure action column is always last
    const actionIndex = sortedColumns.findIndex((col) => col.id === 'action');

    if (actionIndex !== -1) {
        const actionColumn = sortedColumns.splice(actionIndex, 1)[0];
        sortedColumns.push(actionColumn);
    } else {
        sortedColumns.push({
            id: 'action',
            name: '',
            visible: true,
            displayOrder: sortedColumns.length + 1,
        });
    }

    return sortedColumns.map((column) => {
        const isActionColumn = column.id === 'action';

        const headCell: any = {
            ...column,
            sortable: isActionColumn ? false : hasData,
            rowClassName: COLUMN_CLASSNAME_MAP[column.id] || '',
        };

        if (!isActionColumn) {
            const translationKey = COLUMN_TRANSLATION_MAP[column.id];
            headCell.name = translationKey ? t(translationKey) : column.name;
        }

        // Match the action <th> width to the computed body/skeleton action width so header and rows stay aligned
        if (isActionColumn && actionColumnWidthClass) {
            headCell.headCellsTextClass = `${headCell.headCellsTextClass || ''} ${actionColumnWidthClass}`.trim();
        }

        // '!p-0' means the list is loaded and no row renders any action icon (e.g. Booked tab) —
        // hide the whole column instead of leaving an empty sticky strip (same pattern as Orders' hasPriorityOrder).
        if (isActionColumn && actionColumnWidthClass === '!p-0') {
            headCell.visible = false;
        }

        return headCell;
    });
};

/**
 * Transforms API response array of views into IQuoteTab format for tab rendering.
 */
export const formateTabData = (payloadArray: any[]): IQuoteTab[] =>
    payloadArray.map((item) => ({
        name: item.name,
        value: item.id?.toString(),
        id: item?.id,
        count: item.count ?? 0,
        appliedFilters: item.appliedFilters,
        appliedSorting: item.appliedSorting,
        isIconShow: true,
    }));

/**
 * Extracts unique view names from tabs array for duplicate validation.
 */
export const extractViewNames = (tabsArray: IQuoteTab[]): string[] => {
    const names = tabsArray.map((tab) => tab?.name).filter(Boolean);

    return Array.from(new Set(names));
};

/**
 * Formats row data for DraggableLayout column rearrangement preview.
 */
export const formatRowData = (rowData: any[]) => {
    if (rowData.length) {
        const rowArray = rowData.slice(0, 1);
        const formattedData = rowArray.map((quote) => [
            {
                id: 'code',
                name: 'Quote',
                data: {
                    code: quote?.code,
                },
                boxWrapClassName: 'max-w-[13rem]',
            },
            {
                id: 'status',
                name: 'Status',
                data: { status: quote?.status },
            },
            {
                id: 'classification',
                name: 'Service Type',
                data: { classification: quote?.classification },
                boxWrapClassName: 'max-w-[150px]',
            },
            {
                id: 'userData',
                name: 'Created By',
                data: {
                    imageUrl: quote?.userData?.imageUrl,
                    image: quote?.userData?.image,
                    firstName: quote?.userData?.firstName,
                    lastName: quote?.userData?.lastName,
                    fullName: `${quote?.userData?.firstName} ${quote?.userData?.lastName}`,
                },
                boxWrapClassName: 'max-w-[13rem]',
            },
            {
                id: 'division',
                name: 'Division',
                data: { divisionName: quote?.divisionName },
                boxWrapClassName: 'max-w-[150px]',
            },
            {
                id: 'customerBillingAccount',
                name: 'Billing Account',
                data: { billingAccountCurrency: quote?.billingAccountCurrency },
                boxWrapClassName: 'max-w-[160px]',
            },
            {
                id: 'shipperFullAddress',
                name: 'From',
                data: {
                    shipperFullAddress: quote?.shipperFullAddress || '-',
                    shipperCity: quote?.shipperCity || '',
                    shipperStateCode: quote?.shipperStateCode || '',
                },
                boxWrapClassName: 'max-w-[300px]',
            },
            {
                id: 'consigneeFullAddress',
                name: 'To',
                data: {
                    consigneeFullAddress: quote?.consigneeFullAddress || '-',
                    consigneeCity: quote?.consigneeCity || '',
                    consigneeStateCode: quote?.consigneeStateCode || '',
                },
                boxWrapClassName: 'max-w-[300px]',
            },
            {
                id: 'stopsCount',
                name: 'Quote Type',
                data: { stopsCount: quote?.stopsCount },
                boxWrapClassName: 'max-w-[150px]',
            },
            {
                id: 'updatedAt',
                name: 'Last Modify',
                data: { updatedAt: quote?.updatedAt },
                boxWrapClassName: 'max-w-[160px]',
            },
            {
                id: 'createdAt',
                name: 'Creation Date',
                data: { createdAt: quote?.createdAt },
                boxWrapClassName: 'max-w-[160px]',
            },
            {
                id: 'pickupDate',
                name: 'Pickup Date',
                data: { pickupDate: quote?.pickupDate },
                boxWrapClassName: 'max-w-[160px]',
            },
            {
                id: 'totalUnits',
                name: 'Load Details',
                data: {
                    classification: quote?.classification,
                    handlingUnitTypes: quote?.handlingUnitTypes,
                    primaryEquipmentTypeFullName: quote?.primaryEquipmentTypeFullName,
                },
            },
            {
                id: 'totalWeight',
                name: 'Total Weight',
                data: {
                    totalWeight: quote?.totalWeight,
                    weightMeasurement: quote?.weightMeasurement,
                },
            },
        ]);

        return formattedData;
    }

    return [];
};

/**
 * Merges saved column settings from the API with the hardcoded default columns.
 * Ensures any newly-added default columns (not yet in the user's saved settings)
 * appear in the table, at the position the defaults give them.
 */
export const mergeColumnSettings = (savedColumns: any[], defaults: any[]): any[] => {
    if (!savedColumns || !savedColumns.length) return defaults;

    // Keep names from defaults so renamed columns reflect immediately without needing a re-save
    const merged = savedColumns.map((saved: any) => {
        const def = defaults.find((d) => d.id === saved.id);

        return def ? { ...saved, name: def.name } : saved;
    });

    const savedIds = new Set(merged.map((s: any) => s.id));
    const missingDefaults = defaults.filter((col) => !savedIds.has(col.id));

    if (!missingDefaults.length) {
        return merged;
    }

    // New default columns are inserted where `defaults` places them — directly after the
    // nearest preceding sibling the user already has — rather than appended to the end.
    // Anyone with saved settings predates the new column, so appending would silently push
    // it to the far right and ignore the position it was deliberately given in the defaults
    // (e.g. the invoice Due Date column, which must sit beside Status).
    // Saved columns keep their relative order, so a user's own custom arrangement survives.
    const ordered = [...merged].sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    missingDefaults.forEach((col) => {
        const defaultIndex = defaults.findIndex((d) => d.id === col.id);
        // Walk backwards through the defaults to the closest preceding column the user has.
        // No match means nothing precedes it, so it belongs at the front.
        let insertAt = 0;

        for (let i = defaultIndex - 1; i >= 0; i--) {
            const anchorIndex = ordered.findIndex((c: any) => c.id === defaults[i].id);

            if (anchorIndex !== -1) {
                insertAt = anchorIndex + 1;
                break;
            }
        }

        ordered.splice(insertAt, 0, { ...col });
    });

    // Renumber so displayOrder stays contiguous and agrees with the array order
    return ordered.map((col: any, index: number) => ({ ...col, displayOrder: index + 1 }));
};

/**
 * Checks if the current params have changed compared to a saved view's filters/sorting.
 */
export const isParamsChanged = (viewData: any, currentParams: any): boolean => {
    if (!viewData?.appliedFilters) return false;

    const filters = viewData.appliedFilters;
    const sorting = viewData.appliedSorting;

    const fieldsChanged =
        (filters.search || '') !== (currentParams.search || '') ||
        (filters.status || 'All') !== (currentParams.status || 'All') ||
        (filters.classification || null) !== (currentParams.classification || null) ||
        (filters.member || 'all') !== (currentParams.member || 'all');

    const datesChanged = (() => {
        const normalizeDate = (val: any) => (val === '' || val === null || val === undefined ? null : val);

        return normalizeDate(filters.startDate) !== normalizeDate(currentParams.startDate) || normalizeDate(filters.endDate) !== normalizeDate(currentParams.endDate);
    })();

    const sortChanged = (sorting?.sortType || 'desc') !== (currentParams.sortType || 'desc') || (sorting?.sortField || 'updatedAt') !== (currentParams.sortField || 'updatedAt');

    return fieldsChanged || datesChanged || sortChanged;
};

/**
 * Currency + amount a customer should see for one quote rate.
 *
 * The customer's currency is the BILLING ACCOUNT picked at quote creation (`accountCurrency`,
 * returned per row by the quote detail API) — the same rule invoicing uses — with the row's own
 * stamped `currency` as the fallback for quotes that have no billing account. The row stamp alone
 * is NOT trusted first: legacy rows were stamped from the carrier's native currency, so keying on
 * the stamp would flip existing CAD-account quotes to a converted USD price.
 *
 * Guard: when the resolved currency is USD but the row has no USD amount, fall back to the CAD
 * amount + label rather than rendering "$0 USD".
 */
// Index signature so weakly-typed row shapes (QuoteData carries only [key: string]: any) are accepted.
export const getRateCurrencyDisplay = (
    rate: { currency?: string | null; accountCurrency?: string | null; finalCharge?: number | null; finalChargeUSD?: number | null; [key: string]: any } | null | undefined
): { amount: number; label: 'CAD' | 'USD' } => {
    const resolved = (rate?.accountCurrency || rate?.currency || 'CAD').toUpperCase();

    if (resolved === 'USD' && Number(rate?.finalChargeUSD) > 0) {
        return { amount: Number(rate?.finalChargeUSD), label: 'USD' };
    }

    return { amount: Number(rate?.finalCharge) || 0, label: 'CAD' };
};
